import { api } from './api';

// Public API service for fetching data without authentication
export const publicApi = {
  // Events
  getEvents: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    // Pass 'all' to get all events, or use the provided status
    queryParams.append('status', params?.status || 'all');

    // Use relative URL to work with vite proxy
    const url = `/api/v1/events/public?${queryParams}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error('Failed to fetch events:', response.status, response.statusText);
        return { success: false, data: [] };
      }
      const data = await response.json();
      console.log('Public events API response:', data);
      // Handle both direct data array and wrapped response
      if (data.success && Array.isArray(data.data)) {
        return { success: true, data: data.data, pagination: data.pagination };
      } else if (Array.isArray(data)) {
        return { success: true, data: data };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('Error fetching public events:', error);
      return { success: false, data: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  // Notifications
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    priority?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.priority) queryParams.append('priority', params.priority);
    queryParams.append('is_active', 'true');

    const response = await fetch(`/api/v1/public-notifications?${queryParams}`);
    if (!response.ok) {
      return { success: false, data: [] };
    }
    return response.json();
  },

  // Press Releases
  getPressReleases: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    featured?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.featured !== undefined) queryParams.append('featured', params.featured.toString());

    const response = await fetch(`/api/v1/press-releases/public?${queryParams}`);
    if (!response.ok) {
      return { success: false, data: [] };
    }
    return response.json();
  },

  getPressReleaseBySlug: async (slug: string) => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/press-releases/public/${slug}`);
    return response.json();
  },

  // Business Listings
  getBusinessListings: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    verified?: boolean;
    featured?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.verified !== undefined) queryParams.append('verified', params.verified.toString());
    if (params?.featured !== undefined) queryParams.append('featured', params.featured.toString());

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/business/public?${queryParams}`);
    return response.json();
  },

  // Photo Gallery
  getPhotos: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    featured?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.featured !== undefined) queryParams.append('featured', params.featured.toString());

    const response = await fetch(`/api/v1/photo-gallery/public?${queryParams}`);
    return response.json();
  },

  // Community Statistics
  getCommunityStats: async () => {
    const response = await fetch(`/api/v1/community-stats`);
    if (!response.ok) {
      return { success: false, data: null };
    }
    return response.json();
  },
};
